import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactGa from "react-ga4";
import { CircularProgress as JoyCircularProgress } from "@mui/joy";
import styles from "../../styles/winterpay.module.css";
import { ReactComponent as Logo } from "../../svg/newlogo.svg";
import { track, getDistinctId, getAttributionProps } from "../../lib/mixpanel";
import {
    WinterConfig, WinterStudentType, calcCustomerPrice, dayNum, daysInMonth, fromDayNum, toDate, ymd, won, pad2,
} from "./price";
import {
    cx, Calendar, CalCell, ContactSheet, Dropdown, KvList, SegGroup,
    IconCheck, IconChevronDown, IconChevronRight, IconClock, IconCopy, IconHeadset, IconSeat, IconUpload,
} from "./sections";

/**
 * 윈터스쿨 셀프 결제 (/winterpay?id=&code=) — 디자인 "결제링크 copy" 고객 화면
 *  링크(winterPayLink)가 있을 때만 열린다. 고객이 지점·학생구분·프로그램·시작/종료일·학생정보·결제수단을 고르면
 *  서버(peetsunbae.com/dashboard/payments/winterpay/submit)가 금액을 계산해 newPayLink 를 만들고
 *  카드는 결제선생 링크로 보내고, 계좌이체는 계좌 안내 + 인증샷 업로드(기존 uploadpaylinkimg/finishlinkpayment) 로 이어진다.
 *  금액 계산은 ./price.ts (서버와 같은 코드). 인증번호는 registerlink 와 같은 suneungsunbae.com/api/booking/cert.
 */

const API = "https://peetsunbae.com/dashboard/payments";
const CERT_API = "https://suneungsunbae.com/api/booking/cert";
const W = ["일", "월", "화", "수", "목", "금", "토"];
const digits = (v: any) => String(v == null ? "" : v).replace(/\D/g, "");
const phoneOk = (v: string) => /^010\d{8}$/.test(digits(v));
const fmtPhone = (v: string) => { const d = digits(v).slice(0, 11); return d.length > 7 ? d.slice(0, 3) + "-" + d.slice(3, 7) + "-" + d.slice(7) : d.length > 3 ? d.slice(0, 3) + "-" + d.slice(3) : d; };
const dshort = (d: Date) => d.getFullYear() + "." + pad2(d.getMonth() + 1) + "." + pad2(d.getDate()) + "(" + W[d.getDay()] + ")";
const md = (d: Date) => (d.getMonth() + 1) + "." + d.getDate();

const dtLong = (ms: number) => { const d = new Date(ms); return d.getFullYear() + "." + pad2(d.getMonth() + 1) + "." + pad2(d.getDate()) + " " + pad2(d.getHours()) + ":" + pad2(d.getMinutes()); };

interface LinkInfo {
    id: number; location: string | null; studentType: string | null; dueDate: number; status: string; paidAt: number | null;
    program: string | null; startDate: string | null; endDate: string | null; price: number | null; paymentMethod: string | null;
    whichCertify: string | null; studentName: string | null; gender: string | null; studentPhoneNumber: string | null; parentPhoneNumber: string | null;
    newPayLinkId: number | null; newPayLinkCode: number | null; imageSrc: string | null; shortUrl: string | null;
}

type Stage = "pay" | "transfer" | "waiting" | "done";

const WinterPay: React.FC = () => {

    useLayoutEffect(() => { window.scrollTo(0, 0); }, []);

    const params = useMemo(() => new URLSearchParams(window.location.search), []);
    const id = Number(params.get("id"));
    const code = Number(params.get("code"));

    //로딩·링크
    const [loadState, setLoadState] = useState<"loading" | "error" | "ready">("loading");
    const [errorKind, setErrorKind] = useState<string>("");
    const [config, setConfig] = useState<WinterConfig | null>(null);
    const [link, setLink] = useState<LinkInfo | null>(null);
    const [clockOffset, setClockOffset] = useState(0); // 서버시각 - 클라이언트시각
    const [stage, setStage] = useState<Stage>("pay");

    //입력
    const [branch, setBranch] = useState("");
    const [type, setType] = useState<WinterStudentType>("general");
    const [program, setProgram] = useState<string | null>(null);
    const [start, setStart] = useState<Date | null>(null);
    const [end, setEnd] = useState<Date | null>(null);
    const [name, setName] = useState("");
    const [gender, setGender] = useState<"male" | "female">("male");
    const [writer, setWriter] = useState<"parent" | "student">("parent");
    const [studentPhone, setStudentPhone] = useState("");
    const [parentPhone, setParentPhone] = useState("");
    const [verified, setVerified] = useState<string[]>([]);
    const [otpKey, setOtpKey] = useState<string | null>(null);
    const [otpCode, setOtpCode] = useState("");
    const [otpErr, setOtpErr] = useState("");
    const [otpRemain, setOtpRemain] = useState(0);
    const [payMethod, setPayMethod] = useState<"card" | "transfer">("card");

    //UI
    const [openDd, setOpenDd] = useState<string | null>(null);
    const [openRow, setOpenRow] = useState<"start" | "end" | null>(null);
    const [view, setView] = useState<{ y: number; m: number } | null>(null);
    const [showRows, setShowRows] = useState(false);
    const [payTried, setPayTried] = useState(false);
    const [contactOn, setContactOn] = useState(false);
    const [hdrHidden, setHdrHidden] = useState(false);
    const [now, setNow] = useState(Date.now());
    const [vw, setVw] = useState(typeof window !== "undefined" ? window.innerWidth : 390);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitHint, setSubmitHint] = useState("");

    //계좌이체
    const [transfer, setTransfer] = useState<{ newPayLinkId: number; newPayLinkCode: number; bankinfo: any; price: number } | null>(null);
    const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
    const [receiptUploading, setReceiptUploading] = useState(false);
    const [receiptUploaded, setReceiptUploaded] = useState(false);
    const [receiptError, setReceiptError] = useState("");
    const [finishLoading, setFinishLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const codeInputRef = useRef<HTMLInputElement | null>(null);

    const frame = vw >= 1080 ? "p" : vw >= 600 ? "t" : "m";

    //----- 데이터 로드 -----
    const applyLink = (cfg: WinterConfig, lk: LinkInfo) => {
        setConfig(cfg);
        setLink(lk);
        setClockOffset(cfg.now - Date.now());
        if (lk.location) setBranch(lk.location);
        if (lk.studentType === "student" || lk.studentType === "general") setType(lk.studentType);
        //이미 제출한 내용이 있으면 본인이 입력한 값 복원
        if (lk.program) setProgram(lk.program);
        if (lk.startDate) setStart(toDate(lk.startDate));
        if (lk.endDate) setEnd(toDate(lk.endDate));
        if (lk.studentName) setName(lk.studentName);
        if (lk.gender === "male" || lk.gender === "female") setGender(lk.gender);
        if (lk.studentPhoneNumber) setStudentPhone(digits(lk.studentPhoneNumber));
        if (lk.parentPhoneNumber) setParentPhone(digits(lk.parentPhoneNumber));
        if (lk.whichCertify === "student" || lk.whichCertify === "parent") {
            setWriter(lk.whichCertify);
            const num = lk.whichCertify === "student" ? lk.studentPhoneNumber : lk.parentPhoneNumber;
            if (num) setVerified([digits(num)]);
        }
        if (lk.paymentMethod === "withdrawal") setPayMethod("transfer");
        if (lk.paymentMethod === "payteacher") setPayMethod("card");
        if (lk.newPayLinkId && lk.newPayLinkCode && lk.price) {
            const b = cfg.branches.find(x => x.english === lk.location);
            setTransfer({ newPayLinkId: lk.newPayLinkId, newPayLinkCode: lk.newPayLinkCode, bankinfo: b ? b.bankinfo : null, price: lk.price });
        }
        if (lk.imageSrc) { setReceiptPreview("https://peetsunbae.com" + lk.imageSrc); setReceiptUploaded(true); }
        if (lk.status === "paid") setStage("done");
        else if (lk.status === "transferFinished") setStage("waiting");
        else if (lk.status === "transferUploaded") setStage("transfer");
        else setStage("pay");
    };

    const loadData = async () => {
        if (!id || !code) { setErrorKind("noValidCode"); setLoadState("error"); return; }
        try {
            const resp = await fetch(`${API}/winterpay/data?id=${id}&code=${code}`);
            const result = await resp.json();
            if (result.message !== "success") {
                setErrorKind(result.message || "error");
                setLoadState("error");
                track("Winter Pay Link Blocked", { form_type: "winterpay", payment_id: id, reason: result.message });
                return;
            }
            applyLink(result.config, result.link);
            setLoadState("ready");
            track("Winter Pay Page Viewed", { form_type: "winterpay", payment_id: id, status: result.link.status });
        } catch (e) {
            console.log(e);
            setErrorKind("error");
            setLoadState("error");
        }
    };

    useEffect(() => {
        ReactGa.send({ hitType: "pageview", page_title: "winterpay" });
        loadData();
        const tick = setInterval(() => setNow(Date.now()), 1000);
        const onRz = () => setVw(window.innerWidth);
        window.addEventListener("resize", onRz);
        //헤더: 아래로 스크롤하면 숨기고 위로 올리면 다시 보임
        let last = 0;
        const onScroll = () => {
            const y = window.scrollY || document.documentElement.scrollTop || 0;
            if (y > last + 10 && y > 120) setHdrHidden(true);
            else if (y < last - 10 || y <= 0) setHdrHidden(false);
            else return;
            last = y;
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        const onDown = (e: MouseEvent) => { const t = e.target as HTMLElement; if (!(t.closest && t.closest("[data-dd]"))) setOpenDd(null); };
        document.addEventListener("mousedown", onDown);
        return () => { clearInterval(tick); window.removeEventListener("resize", onRz); window.removeEventListener("scroll", onScroll); document.removeEventListener("mousedown", onDown); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //인증번호 남은 시간
    useEffect(() => {
        if (!otpKey || otpRemain <= 0) return;
        const t = setTimeout(() => setOtpRemain(r => r - 1), 1000);
        return () => clearTimeout(t);
    }, [otpKey, otpRemain]);

    //----- 파생값 -----
    const serverNow = now + clockOffset;
    const today = config ? config.today : ymd(new Date(serverNow));
    const todayN = dayNum(toDate(today));
    const linkSec = link ? Math.max(0, Math.floor((link.dueDate - serverNow) / 1000)) : 0;
    const expired = !!link && linkSec <= 0;

    const programs = useMemo(() => (config ? config.programs.filter(p => dayNum(toDate(p.startTo)) >= todayN) : []), [config, todayN]);
    const prog = programs.find(p => p.pid === program) || programs[0] || null;
    const pid = prog ? prog.pid : null;
    const fixed = prog && prog.fixedStart && prog.fixedStart.branches.includes(branch) ? prog.fixedStart.date : null;
    const minN = prog ? Math.max(todayN, dayNum(toDate(prog.startFrom))) : todayN;
    const maxN = prog ? dayNum(toDate(prog.startTo)) : todayN;
    const st0 = fixed ? toDate(fixed) : (start && dayNum(start) >= minN && dayNum(start) <= maxN ? start : null);
    const endOpts = prog ? prog.ends.map(toDate) : [];
    const en0 = st0 && end && endOpts.some(e => dayNum(e) === dayNum(end)) && dayNum(end) >= dayNum(st0) ? end : null;
    const ready = !!(config && branch && st0 && en0);
    const calc = ready && config ? calcCustomerPrice(config.rates, type, branch, st0, en0, today) : null;
    const price = calc ? calc.price : null;
    const original = calc ? calc.original : null;
    const priceOf = (e: Date) => (config && branch && st0 ? calcCustomerPrice(config.rates, type, branch, st0, e, today) : null);
    const branchInfo = config ? config.branches.find(b => b.english === branch) : undefined;

    //프리윈터 좌석 선택권 (11/23~12/12 입실자)
    const seatLastN = config ? dayNum(toDate(config.seatPerk.lastStart)) : 0;
    const seatPerkOn = !!config && programs.some(p => p.pid === "p2") && todayN <= seatLastN;
    const seatOk = seatPerkOn && pid === "p2" && !!st0 && dayNum(st0) <= seatLastN;
    const seatLate = seatPerkOn && pid === "p2" && !!st0 && dayNum(st0) > seatLastN;

    //프로그램 구간(프리/정규) 경계 = 정규윈터 최소 시작일 전날
    const regP = config ? config.programs.find(p => p.pid === "p3") : undefined;
    const regStartN = regP ? dayNum(toDate(regP.startFrom)) : Infinity;
    const endTag = (e: Date) => {
        const n = dayNum(e);
        if (n === regStartN - 1) return "프리윈터 끝까지";
        if (n < regStartN) return "프리윈터 일부";
        const last = regP ? Math.max(...regP.ends.map(x => dayNum(toDate(x)))) : n;
        return n >= last ? "정규윈터 끝까지" : "정규윈터 " + (e.getMonth() + 1) + "월까지";
    };
    const preDays = st0 && en0 ? Math.max(0, Math.min(dayNum(en0), regStartN - 1) - dayNum(st0) + 1) : 0;
    const regDays = st0 && en0 ? Math.max(0, dayNum(en0) - Math.max(dayNum(st0), regStartN) + 1) : 0;
    const spanName = preDays && regDays ? "프리윈터 + 정규윈터" : regDays ? "정규윈터" : preDays ? "프리윈터" : (prog ? prog.name : "");

    //연락처 인증 — 작성자 번호만
    const isVerified = (num: string) => phoneOk(num) && verified.includes(digits(num));
    const writerNum = digits(writer === "student" ? studentPhone : parentPhone);

    //[조건, 안내 문구, 이동할 섹션, 열 행] — 결제 버튼은 항상 활성, 누르면 첫 번째 빠진 섹션으로 이동
    const missing: [boolean, string, string, ("start" | "end")?][] = [
        [!prog, "선택할 수 있는 프로그램이 없어요", "branch"],
        [!branch, "지점을 선택해주세요", "branch"],
        [!st0, "등록 시작일을 선택해주세요", "period", "start"],
        [!!st0 && !en0, "등록 종료일을 선택해주세요", "period", "end"],
        [!name.trim(), "학생 이름을 입력해주세요", "contact"],
        [!phoneOk(studentPhone), "학생 연락처를 입력해주세요", "contact"],
        [!phoneOk(parentPhone), "학부모 연락처를 입력해주세요", "contact"],
        [!isVerified(writerNum), (writer === "student" ? "학생" : "학부모") + " 연락처 인증을 완료해주세요", "contact"],
    ];
    const firstMissing = missing.find(([bad]) => bad) || null;
    const canPay = !firstMissing && price != null && !expired;

    //----- 달력 -----
    const calView = (() => {
        const first0 = fromDayNum(minN);
        return view || { y: first0.getFullYear(), m: first0.getMonth() };
    })();
    const calCells: CalCell[] = (() => {
        const { y, m } = calView;
        const first = new Date(y, m, 1).getDay(), days = daysInMonth(y, m);
        const fixedN = fixed ? dayNum(toDate(fixed)) : null;
        const cells: CalCell[] = [];
        for (let i = 0; i < first; i++) cells.push({ day: "", empty: true, off: true, outside: false, sel: false, today: false, onClick: null });
        for (let d = 1; d <= days; d++) {
            const dt = new Date(y, m, d), n = dayNum(dt), col = (first + d - 1) % 7;
            const outside = n < minN || n > maxN || (fixedN != null && n !== fixedN);
            const off = outside || col === 0;
            cells.push({
                day: d, empty: false, off, outside, sel: !!st0 && dayNum(st0) === n, today: n === todayN,
                onClick: off ? null : () => { setStart(dt); setEnd(prev => (prev && dayNum(prev) >= n ? prev : null)); setOpenRow("end"); },
            });
        }
        while (cells.length < 42) cells.push({ day: "", empty: true, off: true, outside: false, sel: false, today: false, onClick: null });
        return cells;
    })();
    const minD = fromDayNum(minN), maxD = fromDayNum(maxN);
    const calIdx = calView.y * 12 + calView.m;
    const calGo = (k: number) => { const d = new Date(calView.y, calView.m + k, 1); setView({ y: d.getFullYear(), m: d.getMonth() }); };

    //----- 인증번호 -----
    const sendCert = async (num: string, key: string) => {
        if (isVerified(num)) return;
        if (!phoneOk(num)) { setOtpKey(null); setOtpErr(""); setPayTried(true); return; }
        setOtpKey(key + ":" + num); setOtpCode(""); setOtpErr(""); setOtpRemain(300);
        try {
            const resp = await fetch(`${CERT_API}?telephoneNumber=${num}`, { method: "GET" });
            const result = await resp.json();
            console.log(result);
            track("Phone Verification Requested", { form_type: "winterpay", payment_id: id });
        } catch (e) { console.log(e); }
        setTimeout(() => { if (codeInputRef.current) codeInputRef.current.focus(); }, 50);
    };
    const confirmCert = async (num: string, v: string) => {
        if (v.length !== 6) { setOtpErr("인증번호 6자리를 입력해주세요."); return; }
        try {
            const resp = await fetch(CERT_API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ telephoneNumber: num, certNumber: v }) });
            const result = await resp.json();
            if (result.message === "success") {
                setVerified(prev => [...prev, num]); setOtpKey(null); setOtpCode(""); setOtpErr("");
                track("Phone Verification Succeeded", { form_type: "winterpay", payment_id: id });
                if (codeInputRef.current) codeInputRef.current.blur();
            } else {
                setOtpErr("인증번호가 맞지 않아요. 다시 확인해주세요.");
                track("Phone Verification Failed", { form_type: "winterpay", payment_id: id });
            }
        } catch (e) { console.log(e); setOtpErr("인증 확인 중 오류가 났어요. 다시 시도해주세요."); }
    };

    //----- 제출 -----
    const scrollToSec = (sec: string) => {
        const el = document.querySelector(`[data-sec="${sec}"]`);
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 60, behavior: "smooth" });
    };
    const onPay = async () => {
        setSubmitHint("");
        if (!canPay || !config || !st0 || !en0 || !prog) {
            setPayTried(true);
            if (firstMissing) { if (firstMissing[3]) setOpenRow(firstMissing[3]); scrollToSec(firstMissing[2]); }
            track("Winter Pay Attempted", { form_type: "winterpay", payment_id: id, blocked: firstMissing ? firstMissing[1] : (expired ? "expired" : "noPrice") });
            return;
        }
        if (submitLoading) return;
        setSubmitLoading(true);
        try {
            const body = {
                id, code, location: branch, studentType: type, program: prog.pid, startDate: ymd(st0), endDate: ymd(en0),
                name: name.trim(), gender, studentPhoneNumber: digits(studentPhone), parentPhoneNumber: digits(parentPhone), whichCertify: writer,
                paymentMethod: payMethod, clientPrice: price, mp_distinct_id: getDistinctId(), mp_attribution: getAttributionProps(),
            };
            const resp = await fetch(`${API}/winterpay/submit`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
            const result = await resp.json();
            console.log(result);
            setSubmitLoading(false);
            if (result.message === "priceMismatch") {
                if (result.config) { setConfig(result.config); setClockOffset(result.config.now - Date.now()); }
                setSubmitHint("금액 기준이 바뀌어 다시 계산했어요. 금액을 확인하고 다시 눌러주세요.");
                setPayTried(true);
                track("Winter Pay Submit Failed", { form_type: "winterpay", payment_id: id, reason: "priceMismatch" });
                return;
            }
            if (result.message === "isLate") { setSubmitHint("결제 링크가 닫혔어요. 담당자에게 새 링크를 요청해 주세요."); track("Winter Pay Submit Failed", { form_type: "winterpay", payment_id: id, reason: "expired" }); return; }
            if (result.message === "isPaid") { await loadData(); return; }
            if (result.message === "destroyed") { setErrorKind("destroyed"); setLoadState("error"); return; }
            if (result.message !== "success") {
                setSubmitHint("오류가 났어요. 잠시 후 다시 시도하거나 상담으로 문의해주세요.");
                track("Winter Pay Submit Failed", { form_type: "winterpay", payment_id: id, reason: result.message });
                return;
            }
            track("Winter Pay Submitted", { form_type: "winterpay", payment_id: id, payment_method: payMethod, price, branch, program: prog.pid });
            if (result.transfer) {
                setTransfer({ newPayLinkId: result.newPayLinkId, newPayLinkCode: result.newPayLinkCode, bankinfo: result.bankinfo, price: result.price });
                if (result.imageSrc) { setReceiptPreview("https://peetsunbae.com" + result.imageSrc); setReceiptUploaded(true); }
                setStage("transfer");
                window.scrollTo(0, 0);
                track("Payment Bank Info Shown", { form_type: "winterpay", payment_id: id });
                return;
            }
            if (result.shortURL) {
                track("Payment Card Redirect", { form_type: "winterpay", payment_id: id, payment_method: "card" });
                window.location.href = result.shortURL;
            }
        } catch (e) {
            console.log(e);
            setSubmitLoading(false);
            setSubmitHint("오류가 났어요. 잠시 후 다시 시도하거나 상담으로 문의해주세요.");
        }
    };

    //----- 계좌이체: 인증샷 업로드 · 입금 완료 -----
    const uploadReceipt = async (file: File) => {
        if (!transfer) return;
        setReceiptError(""); setReceiptUploading(true); setReceiptUploaded(false);
        setReceiptPreview(URL.createObjectURL(file));
        track("Receipt Upload Started", { form_type: "winterpay", payment_id: id, file_size: file.size });
        try {
            const form = new FormData();
            form.append("file", file);
            form.append("data", JSON.stringify({ id: transfer.newPayLinkId, paymentMethod: "withdrawal", code: transfer.newPayLinkCode }));
            const resp = await fetch(`${API}/uploadpaylinkimg`, { method: "POST", body: form });
            const result = await resp.json();
            if (result.message === "success") {
                setReceiptUploaded(true);
                track("Receipt Upload Succeeded", { form_type: "winterpay", payment_id: id });
            } else {
                setReceiptPreview(null);
                setReceiptError(result.message === "expired" ? "결제 링크가 닫혔어요. 담당자에게 새 링크를 요청해 주세요." : result.message === "destroyed" ? "파기된 링크예요." : "업로드에 실패했어요. 다시 시도해주세요.");
                track("Receipt Upload Failed", { form_type: "winterpay", payment_id: id, reason: result.message });
            }
        } catch (e) {
            console.log(e);
            setReceiptPreview(null);
            setReceiptError("업로드에 실패했어요. 다시 시도해주세요.");
        }
        setReceiptUploading(false);
    };
    const finishTransfer = async () => {
        if (!transfer || !receiptUploaded || finishLoading) return;
        setFinishLoading(true);
        try {
            const resp = await fetch(`${API}/finishlinkpayment`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: transfer.newPayLinkId, code: transfer.newPayLinkCode }) });
            const result = await resp.json();
            if (result.message === "success") {
                track("Payment Self Reported", { form_type: "winterpay", payment_id: id });
                setStage("waiting");
                window.scrollTo(0, 0);
            } else {
                setReceiptError("처리에 실패했어요. 다시 시도해주세요.");
            }
        } catch (e) { console.log(e); setReceiptError("처리에 실패했어요. 다시 시도해주세요."); }
        setFinishLoading(false);
    };
    const copyAccount = async () => {
        if (!transfer || !transfer.bankinfo) return;
        try { await navigator.clipboard.writeText(String(transfer.bankinfo.accountNumber).replace(/-/g, "")); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) { console.log(e); }
    };

    //----- 화면 -----
    const pageClass = cx(styles.page, frame === "t" && styles.frameT, frame === "p" && styles.frameP);

    const Header = (
        <header className={cx(styles.hdr, hdrHidden && styles.hdrHidden)}>
            <span className={styles.logo} aria-label="수능선배"><Logo /></span>
            <button type="button" className={styles.hdrBtn} aria-label="상담 매체 선택" onClick={() => setContactOn(true)}><IconHeadset /></button>
        </header>
    );

    if (loadState === "loading") {
        return <div className={pageClass}><div className={styles.inner}>{Header}<div className={styles.loading}><JoyCircularProgress size="lg" thickness={6} /></div></div></div>;
    }

    if (loadState === "error" || !config || !link) {
        const msg = errorKind === "expired" ? "결제 링크가 닫혔어요" : errorKind === "destroyed" ? "파기된 링크예요" : errorKind === "noValidCode" || errorKind === "noData" ? "잘못된 접근이에요" : "링크를 불러오지 못했어요";
        const sub = errorKind === "expired" ? "담당자에게 새 링크를 요청해 주세요." : errorKind === "destroyed" ? "담당자에게 새 링크를 요청해 주세요." : "링크 주소를 다시 확인하거나 상담으로 문의해주세요.";
        return (
            <div className={pageClass}><div className={styles.inner}>
                {Header}
                {contactOn && <ContactSheet onClose={() => setContactOn(false)} />}
                <div className={styles.errorScreen}>
                    <div className={styles.errorBanner}><IconClock />{msg}</div>
                    <p className={styles.doneSub}>{sub}</p>
                    <button type="button" className={styles.secondaryBtn} style={{ maxWidth: 320 }} onClick={() => setContactOn(true)}>상담 문의하기</button>
                </div>
            </div></div>
        );
    }

    const branchKo = branchInfo ? branchInfo.korean : (link.location || "");
    const periodTxt = st0 && en0 ? dshort(st0) + " ~ " + dshort(en0) : "-";
    const totalTxt = price == null ? "-" : won(price);

    //타이머
    const expAt = new Date(link.dueDate);
    const dueTxt = (() => {
        const t0 = new Date(serverNow); t0.setHours(0, 0, 0, 0);
        const e0 = new Date(expAt); e0.setHours(0, 0, 0, 0);
        const diff = Math.round((e0.getTime() - t0.getTime()) / 86400000);
        const day = diff === 0 ? "오늘" : diff === 1 ? "내일" : (expAt.getMonth() + 1) + "월 " + expAt.getDate() + "일";
        const h = expAt.getHours(), ap = h < 12 ? "오전" : "오후", h12 = h % 12 || 12;
        return day + " " + ap + " " + h12 + ":" + pad2(expAt.getMinutes()) + "까지";
    })();
    const remainTxt = pad2(Math.floor(linkSec / 3600)) + ":" + pad2(Math.floor(linkSec % 3600 / 60)) + ":" + pad2(linkSec % 60);

    //----- 결제완료 -----
    if (stage === "done") {
        const methodLabel = link.paymentMethod === "withdrawal" ? "계좌이체" : "신용·체크카드";
        const rows: [string, string][] = [
            ["결제 금액", link.price ? won(link.price) : totalTxt],
            ["결제 수단", methodLabel],
            ["결제 일시", link.paidAt ? dtLong(link.paidAt) : "-"],
            ["프로그램", spanName || "-"],
            ["학생", name.trim() || "-"],
            ["지점", branchKo || "-"],
            ["등록 기간", periodTxt],
        ];
        return (
            <div className={pageClass}><div className={styles.inner}>
                {Header}
                {contactOn && <ContactSheet onClose={() => setContactOn(false)} />}
                <div className={styles.doneWrap}>
                    <span className={styles.doneIcon}><IconCheck size={28} stroke="#fff" /></span>
                    <h1 className={styles.doneTitle}>등록이 완료되었어요</h1>
                    <p className={styles.doneSub}>{name.trim() ? name.trim() + " 학생의 " : ""}윈터스쿨 등록비 결제가 완료되었어요.</p>
                </div>
                <div className={styles.bottomPad}><KvList rows={rows} /></div>
            </div></div>
        );
    }

    //----- 입금 확인 대기 -----
    if (stage === "waiting") {
        const rows: [string, string][] = [
            ["입금 금액", transfer ? won(transfer.price) : totalTxt],
            ["입금 계좌", transfer && transfer.bankinfo ? transfer.bankinfo.bankName + " " + transfer.bankinfo.accountNumber : "-"],
            ["학생", name.trim() || "-"],
            ["지점", branchKo || "-"],
            ["등록 기간", periodTxt],
        ];
        return (
            <div className={pageClass}><div className={styles.inner}>
                {Header}
                {contactOn && <ContactSheet onClose={() => setContactOn(false)} />}
                <div className={styles.doneWrap}>
                    <span className={cx(styles.doneIcon, styles.doneIconSoft)}><IconClock /></span>
                    <h1 className={styles.doneTitle}>입금 확인을 기다리고 있어요</h1>
                    <p className={styles.doneSub}>직원이 입금 내역을 확인하면 등록이 완료돼요. 확인되는 대로 안내를 보내드려요.</p>
                </div>
                <div className={styles.bottomPad}>
                    <KvList rows={rows} />
                    {receiptPreview && <div style={{ marginTop: 24 }}><span className={styles.label}>올린 인증샷</span><img src={receiptPreview} alt="입금 인증샷" className={styles.preview} style={{ marginTop: 8 }} /></div>}
                </div>
            </div></div>
        );
    }

    //----- 계좌이체 -----
    if (stage === "transfer" && transfer) {
        const bi = transfer.bankinfo;
        return (
            <div className={pageClass}><div className={styles.inner}>
                {Header}
                {contactOn && <ContactSheet onClose={() => setContactOn(false)} />}
                <div className={styles.pad}><h1 className={styles.title}>계좌이체로 결제하기</h1></div>
                <div className={cx(styles.timer, expired && styles.timerExpired, expired && styles.timerStatic)}>
                    <span className={styles.timerMain}><IconClock />{expired ? <span className={styles.timerTail}>결제 링크가 닫혔어요. 담당자에게 새 링크를 요청해 주세요</span> : <><span className={styles.timerRemain}>{remainTxt}</span><span className={styles.timerTail}>안에 입금해 주세요 · {dueTxt}</span></>}</span>
                </div>
                <div className={styles.sec}>
                    <span className={styles.secTitle}>입금 정보</span>
                    <div className={styles.amountBox}>
                        <span className={styles.amountLabel}>입금할 금액</span>
                        <span className={styles.amountRight}><span className={styles.amountVal}>{won(transfer.price)}</span></span>
                        <span className={styles.split}>{periodTxt} · {spanName}</span>
                    </div>
                    <div className={styles.bankBox}>
                        <div className={styles.bankRow}><span className={styles.bankKey}>은행</span><span className={styles.bankVal}>{bi ? bi.bankName : "-"}</span></div>
                        <div className={styles.bankRow}><span className={styles.bankKey}>계좌번호</span><span className={styles.bankVal} style={{ display: "flex", alignItems: "center", gap: 8 }}>{bi ? bi.accountNumber : "-"}<button type="button" className={styles.copyBtn} onClick={copyAccount}><IconCopy />{copied ? "복사됨" : "복사"}</button></span></div>
                        <div className={styles.bankRow}><span className={styles.bankKey}>예금주</span><span className={styles.bankVal}>{bi ? bi.accountName : "-"}</span></div>
                        <div className={styles.bankRow}><span className={styles.bankKey}>입금자명</span><span className={styles.bankVal}>{name.trim() || "학생 이름"}</span></div>
                    </div>
                    <div className={styles.stepList}>
                        <div className={styles.step}><span className={styles.stepNo}>1</span><span>위 계좌로 <b>{won(transfer.price)}</b>을 입금해 주세요. 입금자명은 학생 이름으로 해주세요.</span></div>
                        <div className={styles.step}><span className={styles.stepNo}>2</span><span>이체 완료 화면을 캡처해서 아래에 올려주세요.</span></div>
                        <div className={styles.step}><span className={styles.stepNo}>3</span><span>'입금했어요'를 누르면 직원이 확인 후 등록을 완료해 드려요.</span></div>
                    </div>
                </div>
                <div className={styles.sec} data-sec="receipt">
                    <span className={styles.secTitle}>이체 인증샷</span>
                    <input ref={fileInputRef} type="file" accept="image/*" className={styles.hiddenInput} onChange={e => { const f = e.target.files && e.target.files[0]; if (f) uploadReceipt(f); e.target.value = ""; }} />
                    <div role="button" tabIndex={0} className={cx(styles.uploadArea, receiptUploaded && styles.uploadAreaDone)} onClick={() => { if (!receiptUploading && !expired && fileInputRef.current) fileInputRef.current.click(); }} onKeyDown={e => { if (e.key === "Enter" && fileInputRef.current) fileInputRef.current.click(); }}>
                        {receiptUploading ? <JoyCircularProgress size="sm" /> : receiptUploaded ? <IconCheck size={24} stroke="#0A1931" /> : <IconUpload />}
                        <span className={styles.uploadTitle}>{receiptUploading ? "올리는 중이에요" : receiptUploaded ? "인증샷을 올렸어요" : "이체 완료 화면 올리기"}</span>
                        <span className={styles.mute}>{receiptUploaded ? "다른 사진으로 바꾸려면 다시 눌러주세요" : "사진을 고르면 바로 올라가요"}</span>
                    </div>
                    {receiptPreview && <img src={receiptPreview} alt="이체 인증샷 미리보기" className={styles.preview} />}
                    {receiptError && <span className={styles.err}>{receiptError}</span>}
                </div>
                <div className={styles.refund}><p>입실 전까지는 전액 환불돼요. 입금이 확인되면 입실 안내 문자를 학생·학부모 번호로 보내드려요.</p></div>
                <div className={styles.stickyBar}>
                    <button type="button" className={styles.payBtn} disabled={!receiptUploaded || receiptUploading || finishLoading || expired} onClick={finishTransfer}>
                        {finishLoading ? <JoyCircularProgress size="sm" /> : <span className={styles.payLabel}>{receiptUploaded ? "입금했어요" : "인증샷을 올리면 눌러요"}</span>}
                    </button>
                    <button type="button" className={styles.secondaryBtn} style={{ pointerEvents: "auto" }} onClick={() => { setStage("pay"); window.scrollTo(0, 0); }}>결제 정보 다시 고르기</button>
                </div>
            </div></div>
        );
    }

    //----- 결제 화면 -----
    const branchOpts = config.branches.map(b => ({ value: b.english, label: b.korean }));
    const monthly = (() => {
        const row = config.rates.prices[branch];
        if (!row) return [];
        const per = row.periods || {};
        const pick = (pidKey: string) => { const v = per[pidKey]; return v != null && v > 0 ? v : row.base; };
        const pre = type === "student" ? pick("p4") : pick("p2");
        return [["프리윈터", pre], ["정규윈터", pick("p3")]] as [string, number][];
    })();
    const endList = endOpts.filter(e => st0 && dayNum(e) >= dayNum(st0)).map(e => {
        const r = priceOf(e);
        const pr = r ? r.price : null, orig = r ? r.original : null;
        const disc = pr != null && orig != null && orig > pr;
        return { e, active: !!en0 && dayNum(e) === dayNum(en0), pr, disc, pct: disc && pr != null && orig != null ? Math.round((orig - pr) / orig * 100) + "%" : "", orig };
    });
    const hasOrig = price != null && original != null && original > price;
    const pctTxt = hasOrig && price != null && original != null ? Math.round((1 - price / original) * 100) + "%" : "";
    const benefitTexts: string[] = calc && calc.usedBenefits.length ? [calc.usedBenefits.map(c => c.name + " " + c.pct + "%").join(" · ")] : [];
    const perks = seatOk ? [...benefitTexts, "희망 좌석 선택권 · 프리윈터 전용"] : benefitTexts;
    const hasHint = (!!firstMissing && payTried) || !!submitHint;
    const hintText = submitHint || (firstMissing ? firstMissing[1] : "");
    const phones = (writer === "parent" ? [["parent", "학부모 연락처"], ["phone", "학생 연락처"]] : [["phone", "학생 연락처"], ["parent", "학부모 연락처"]]) as [string, string][];

    return (
        <div className={pageClass}><div className={styles.inner}>
            {Header}
            {contactOn && <ContactSheet onClose={() => setContactOn(false)} />}

            <div className={styles.pad}><h1 className={styles.title}>윈터스쿨 등록하기</h1></div>

            <div className={cx(styles.timer, expired && styles.timerExpired, expired && styles.timerStatic)}>
                <span className={styles.timerMain}>
                    <IconClock />
                    {expired
                        ? <span className={styles.timerTail}>결제 링크가 닫혔어요. 담당자에게 새 링크를 요청해 주세요</span>
                        : <><span className={styles.timerRemain}>{remainTxt}</span><span className={styles.timerTail}>후 결제 링크가 닫혀요 · {dueTxt}</span></>}
                </span>
                {!expired && <span className={styles.timerSub}>빨리 결제할수록 등록 순서도 빨라져요</span>}
            </div>

            {/* 등록 지점 */}
            <div className={styles.sec} data-sec="branch">
                <span className={styles.secTitle}>등록 지점</span>
                <div className={styles.col}>
                    <div className={styles.field}>
                        <span className={styles.label}>지점</span>
                        <Dropdown value={branch} options={branchOpts} placeholder="지점을 선택해주세요" open={openDd === "branch"} onToggle={() => setOpenDd(openDd === "branch" ? null : "branch")} onPick={v => { setBranch(v); setOpenDd(null); setStart(null); setEnd(null); }} />
                    </div>
                    <div className={styles.field}>
                        <span className={styles.label}>학생 구분</span>
                        <SegGroup items={[{ value: "general", label: "N수생" }, { value: "student", label: "재학생" }]} value={type} onChange={v => setType(v as WinterStudentType)} />
                    </div>
                    {branch && (
                        <div className={styles.monthlyBox}>
                            <span className={styles.monthlyHead}>1개월 기준 등록비</span>
                            {monthly.map(([label, v]) => <div className={styles.monthlyRow} key={label}><span className={styles.monthlyLabel}>{label}</span><span className={styles.monthlyPrice}>{v ? won(v) : "-"}</span></div>)}
                            <span className={styles.mute}>입실일 기준으로 일할 계산돼요.</span>
                        </div>
                    )}
                </div>
            </div>

            {/* 등록 기간 */}
            {branch && (
                <div className={styles.sec} data-sec="period">
                    <span className={styles.secTitle}>등록 기간</span>
                    <div className={styles.field}>
                        <span className={styles.label}>언제부터 다닐까요?</span>
                        <div role="radiogroup" className={styles.progList}>
                            {programs.map(p => {
                                const a = p.pid === pid, isPre = p.pid === "p2", sd = toDate(p.startFrom);
                                return (
                                    <button type="button" role="radio" aria-checked={a} key={p.pid} className={cx(styles.prog, a && styles.progOn)} onClick={() => { if (!a) { setProgram(p.pid); setStart(null); setEnd(null); setView(null); setOpenRow(null); } }}>
                                        <span className={styles.radio}><span className={styles.radioDot} /></span>
                                        <span className={styles.progHead}>
                                            <span className={styles.progTitle}>{p.name}</span>
                                            <span className={styles.progPeriod}>{(sd.getMonth() + 1) + "." + sd.getDate()} 시작</span>
                                            {isPre && seatPerkOn && <span className={styles.perkTag}>프리윈터 전용 혜택</span>}
                                        </span>
                                        {isPre && seatPerkOn && (
                                            <span className={styles.perkBox}>
                                                <IconSeat />
                                                <span><span className={styles.perkTitle}>희망 좌석 선택권</span><span className={styles.perkSub}>원하는 좌석을 직접 골라요</span></span>
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className={styles.field}>
                        <button type="button" aria-expanded={openRow === "start"} className={cx(styles.rowSel, openRow === "start" && styles.rowSelOpen)} onClick={() => setOpenRow(openRow === "start" ? null : "start")}>
                            <span className={styles.rowLabel}>시작일</span>
                            <span className={cx(styles.rowVal, !st0 && styles.rowValEmpty)}>{st0 ? dshort(st0) : "선택"}<IconChevronRight /></span>
                        </button>
                        {seatLate && (
                            <div className={styles.seatNote}>
                                <span className={styles.seatNoteText}>12.12까지 입실해야 좌석을 직접 고를 수 있어요</span>
                                <button type="button" className={styles.seatCta} onClick={() => setOpenRow("start")}>날짜 바꾸기</button>
                            </div>
                        )}
                        {openRow === "start" && (
                            <Calendar title={calView.y + "년 " + (calView.m + 1) + "월"} cells={calCells}
                                prevVis={calIdx > minD.getFullYear() * 12 + minD.getMonth()} nextVis={calIdx < maxD.getFullYear() * 12 + maxD.getMonth()}
                                onPrev={() => calGo(-1)} onNext={() => calGo(1)} />
                        )}
                        {fixed && <span className={styles.mute}>※ {branchKo} {md(toDate(fixed))} 시작 고정</span>}
                        {!fixed && prog && <span className={styles.mute}>※ 시작일 {md(toDate(prog.startFrom))} ~ {md(toDate(prog.startTo))} 중 선택 (일요일 제외)</span>}
                        <button type="button" aria-expanded={openRow === "end"} disabled={!st0} className={cx(styles.rowSel, openRow === "end" && styles.rowSelOpen)} onClick={() => setOpenRow(openRow === "end" ? null : "end")}>
                            <span className={styles.rowLabel}>종료일</span>
                            <span className={cx(styles.rowVal, !en0 && styles.rowValEmpty)}>{en0 ? dshort(en0) : "선택"}<IconChevronRight /></span>
                        </button>
                        {openRow === "end" && st0 && (
                            <div role="radiogroup" className={styles.endList}>
                                {endList.map(eo => (
                                    <button type="button" role="radio" aria-checked={eo.active} key={ymd(eo.e)} className={cx(styles.endItem, eo.active && styles.endItemOn)} onClick={() => { setEnd(eo.e); setOpenRow(null); }}>
                                        <span><span className={styles.endDate}>{dshort(eo.e)}</span><span className={styles.endTag}>{endTag(eo.e)}</span></span>
                                        <span className={styles.endRight}>
                                            <span className={styles.endPrice}>{eo.disc && <><span className={styles.endOrig}>{eo.orig != null ? won(eo.orig) : ""}</span><span className={styles.endPct}>{eo.pct}</span></>}{eo.pr == null ? "-" : won(eo.pr)}</span>
                                            <span style={{ color: eo.active ? "#0A1931" : "#8B95A1", display: "flex" }}>{eo.active ? <IconCheck width={1.75} /> : <IconChevronRight />}</span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                        {st0 && en0 && <span className={styles.mute}>{dshort(st0)} ~ {dshort(en0)} · {preDays && regDays ? "프리 " + preDays + "일 + 정규 " + regDays + "일" : "총 " + (preDays + regDays) + "일"}</span>}
                    </div>
                </div>
            )}

            {/* 학생 정보 */}
            <div className={styles.sec} data-sec="contact">
                <span className={styles.secTitle}>학생 정보</span>
                <div className={styles.col}>
                    <div className={styles.gridAuto}>
                        <div className={styles.field}>
                            <span className={styles.label}>학생 이름</span>
                            <input value={name} onChange={e => setName(e.target.value)} placeholder="이름" autoComplete="off" enterKeyHint="next" className={cx(styles.input, payTried && !name.trim() && styles.inputErr)} />
                            {payTried && !name.trim() && <span className={styles.err}>학생 이름을 입력해주세요</span>}
                        </div>
                        <div className={styles.field}>
                            <span className={styles.label}>성별</span>
                            <SegGroup items={[{ value: "male", label: "남학생" }, { value: "female", label: "여학생" }]} value={gender} onChange={v => setGender(v as "male" | "female")} />
                        </div>
                    </div>
                    <div className={styles.field}>
                        <span className={styles.label}>누가 작성하고 있나요?</span>
                        <SegGroup items={[{ value: "parent", label: "학부모" }, { value: "student", label: "학생 본인" }]} value={writer} onChange={v => { setWriter(v as "parent" | "student"); setOtpKey(null); setOtpCode(""); setOtpErr(""); }} />
                    </div>
                    {phones.map(([k, label]) => {
                        const val = k === "phone" ? studentPhone : parentPhone;
                        const num = digits(val);
                        const mine = (writer === "student" ? "phone" : "parent") === k;
                        const ok = mine && isVerified(num);
                        const sent = mine && !ok && otpKey === k + ":" + num;
                        const err = num.length >= 3 && !num.startsWith("010") ? "010으로 시작하는 번호를 입력해주세요" : payTried && !phoneOk(num) ? label + "를 입력해주세요" : "";
                        return (
                            <div className={styles.field} key={k}>
                                <span className={styles.label}>{label}</span>
                                <div className={styles.phoneRow}>
                                    <input value={fmtPhone(val)} onChange={e => { const d = digits(e.target.value).slice(0, 11); if (k === "phone") setStudentPhone(d); else setParentPhone(d); }} placeholder="010-0000-0000" type="tel" inputMode="numeric" autoComplete={mine ? "tel" : "off"} className={cx(styles.input, err && styles.inputErr)} />
                                    {mine && (
                                        <button type="button" className={cx(styles.certBtn, phoneOk(num) && styles.certBtnReady, ok && styles.certBtnDone)} onClick={() => sendCert(num, k)}>
                                            {ok ? <><IconCheck size={14} width={2.25} />인증 완료</> : sent ? "다시 받기" : "인증번호 받기"}
                                        </button>
                                    )}
                                </div>
                                {err && <span className={styles.err}>{err}</span>}
                                {sent && (
                                    <>
                                        <div className={styles.phoneRow}>
                                            <input ref={codeInputRef} value={otpCode} onChange={e => { const v = digits(e.target.value).slice(0, 6); setOtpCode(v); setOtpErr(""); if (v.length === 6) confirmCert(num, v); }} placeholder="인증번호 6자리" inputMode="numeric" autoComplete="one-time-code" maxLength={6} className={cx(styles.input, otpErr && styles.inputErr)} />
                                            <button type="button" className={styles.confirmBtn} onClick={() => confirmCert(num, otpCode)}>확인</button>
                                        </div>
                                        <span className={cx(styles.codeMsg, otpErr && styles.codeMsgErr)}>{otpErr || (otpRemain > 0 ? "인증번호를 보냈어요. " + Math.floor(otpRemain / 60) + ":" + pad2(otpRemain % 60) + " 안에 입력해주세요." : "인증번호 유효 시간이 지났어요. 다시 받아주세요.")}</span>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* 결제 수단 */}
            <div className={styles.sec} data-sec="method">
                <span className={styles.secTitle}>결제 수단</span>
                <div role="radiogroup" className={styles.progList}>
                    {([["card", "신용·체크카드"], ["transfer", "계좌이체"]] as [("card" | "transfer"), string][]).map(([k, label]) => (
                        <button type="button" role="radio" aria-checked={payMethod === k} key={k} className={cx(styles.method, payMethod === k && styles.methodOn)} onClick={() => setPayMethod(k)}>
                            <span className={styles.methodRing}><span className={styles.methodDot} /></span>
                            <span className={styles.methodLabel}>{label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* 결제 금액 */}
            <div className={styles.sec} data-sec="amount">
                <span className={styles.secTitle}>결제 금액</span>
                <div className={styles.amountBox}>
                    <span className={styles.amountLabel}>최종 결제 금액</span>
                    <span className={styles.amountRight}>
                        {hasOrig && <><span className={styles.orig}>{won(original as number)}</span><span className={styles.pct}>{pctTxt}</span></>}
                        <span className={cx(styles.amountVal, price == null && styles.amountValEmpty)}>{totalTxt}</span>
                    </span>
                    {st0 && en0 && <span className={styles.split}>{dshort(st0)} ~ {dshort(en0)} · {preDays && regDays ? "프리 " + preDays + "일 + 정규 " + regDays + "일" : "총 " + (preDays + regDays) + "일"}</span>}
                </div>
                {perks.length > 0 && (
                    <div className={styles.benefitBox}>
                        <span className={styles.benefitHead}>적용된 혜택</span>
                        {perks.map(b => <span className={styles.benefitItem} key={b}>{b}</span>)}
                    </div>
                )}
                {calc && calc.rows.length > 0 && (
                    <button type="button" aria-expanded={showRows} className={cx(styles.rowsToggle, showRows && styles.rowsToggleOpen)} onClick={() => setShowRows(s => !s)}><IconChevronDown stroke="currentColor" />월별 계산 내역 {showRows ? "접기" : "보기"}</button>
                )}
                {calc && showRows && (
                    <div className={styles.rowsList}>
                        {calc.rows.map((r, i) => (
                            <div className={styles.rowItem} key={i}>
                                <div><span className={styles.rowMain}>{r.month}</span><span className={styles.rowSub}>{r.range}</span></div>
                                <div><span className={styles.rowMain}>{r.amount}</span><span className={styles.rowSub}>{r.days}</span></div>
                            </div>
                        ))}
                        <div className={styles.noteBox}>
                            <span className={styles.noteHead}>계산 방법</span>
                            <div className={styles.noteRow}><span>한 달 전체</span><span>월 금액 그대로</span></div>
                            <div className={styles.noteRow}><span>일부만 다닌 달</span><span>월 금액 ÷ 그 달 일수 × 다닌 날</span></div>
                            {benefitTexts.length > 0 && <div className={styles.noteRow}><span>할인</span><span>월 금액에 먼저 적용</span></div>}
                            <div className={styles.noteRow}><span>끝자리</span><span>10원 미만은 버려요</span></div>
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.refund}><p>입실 전까지는 전액 환불돼요. 결제하면 입실 안내 문자를 학생·학부모 번호로 보내드려요.</p></div>

            <div className={styles.stickyBar}>
                {hasHint && <span className={styles.hint}>{hintText}</span>}
                {!hasHint && !expired && <span className={styles.orderNote}><IconCheck size={14} width={2.25} />{price != null && st0 && en0 ? "입실 전까지 전액 환불" : "입실 전까지 전액 환불돼요"}</span>}
                {!hasHint && link.shortUrl && link.status === "submitted" && payMethod === "card" && !expired && (
                    <button type="button" className={styles.orderNote} style={{ cursor: "pointer" }} onClick={() => { track("Payment Card Redirect", { form_type: "winterpay", payment_id: id, payment_method: "card", resumed: true }); window.location.href = link.shortUrl as string; }}>이전에 만든 결제 링크로 바로 가기</button>
                )}
                <button type="button" className={styles.payBtn} disabled={expired || submitLoading} onClick={onPay}>
                    {submitLoading ? <JoyCircularProgress size="sm" /> : <>
                        {seatOk && price != null && <span className={styles.payBadge}>좌석 선택권</span>}
                        {hasOrig && <span className={styles.payOrig}>{won(original as number)}</span>}
                        <span className={styles.payLabel}>{price == null ? "결제하기" : won(price) + " 결제하기"}</span>
                    </>}
                </button>
            </div>
        </div></div>
    );
};

export default WinterPay;
